import PageHeader from '../../components/PageHeader';
import FilterPanel from '../../components/FilterPanel';
import { useEffect, useState } from 'react';
import DataTable from '../../components/DataTable';
import { formatDate } from '../../utils/formatters';
import { PolygonEditor } from '../PlacesPage/PlaceDialog';
import OpeningHoursEditor from '../PlacesPage/OpeningHoursEditor';
import { normalizeOpeningHours, serializeOpeningHours } from '../../constants/openingHours';
import { Button, Typography, MenuItem, Stack, TextField, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
const statusLabel = {
  PENDING: 'ממתין',
  APPROVED: 'אושר',
  REJECTED: 'נדחה',
};
const footprintParts = (footprint) => {
  const polygons =
    footprint?.type === 'MultiPolygon'
      ? footprint.coordinates || []
      : footprint?.type === 'Polygon'
        ? [footprint.coordinates || []]
        : [];
  return polygons
    .map((polygon) => {
      const ring = polygon?.[0] || [];
      return ring.length > 1 ? ring.slice(0, -1) : [];
    })
    .filter((part) => part.length >= 3);
};
const SubmissionDialog = ({
  submission,
  onClose,
  onSave,
  onApprove,
  onReject,
  onBlock,
  loading,
}) => {
  const [proposed, setProposed] = useState(submission.proposed);
  const [adminNote, setAdminNote] = useState(submission.adminNote || '');
  const [hours, setHours] = useState([]);
  const [parts, setParts] = useState([]);
  const [error, setError] = useState('');
  useEffect(() => {
    setProposed(submission.proposed);
    setAdminNote(submission.adminNote || '');
    setHours(normalizeOpeningHours(submission.proposed?.openingHours));
    setParts(footprintParts(submission.proposed?.footprint));
  }, [submission]);
  const payload = () => {
    try {
      return {
        proposed: {
          ...proposed,
          openingHours: serializeOpeningHours(hours),
          footprint:
            parts.length === 0
              ? null
              : parts.length === 1
                ? {
                    type: 'Polygon',
                    coordinates: [[...parts[0], parts[0][0]]],
                  }
                : {
                    type: 'MultiPolygon',
                    coordinates: parts.map((part) => [[...part, part[0]]]),
                  },
        },
        adminNote,
      };
    } catch {
      setError('לא ניתן להכין את השינויים לשמירה');
      return null;
    }
  };
  const run = async (action) => {
    const data = payload();
    if (!data) return;
    try {
      await action(submission.submissionId, data);
      onClose();
    } catch (actionError) {
      setError(actionError.message || 'הפעולה נכשלה');
    }
  };
  const update = (key, value) =>
    setProposed((current) => ({
      ...current,
      [key]: value,
    }));
  return (
    <Stack
      onMouseDown={onClose}
      sx={{
        position: 'fixed',
        inset: '0',
        zIndex: '30',
        display: 'grid',
        placeItems: 'center',
        padding: '24px',
        background: 'rgba(21, 33, 31, .56)',
        backdropFilter: 'blur(6px)',
      }}
    >
      <Stack
        onMouseDown={(event) => event.stopPropagation()}
        sx={(theme) => ({
          width: '100%',
          height: '100%',
          display: 'grid',
          background: 'var(--panel)',
          borderRadius: '20px',
          boxShadow: 'var(--shadow-lg)',
          overflow: 'hidden',
          textAlign: 'right',
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            height: '100vh',
            maxHeight: '100vh',
            minHeight: '100vh',
            borderRadius: '0',
          },
          border: '1px solid rgba(255,255,255,.45)',
        })}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            padding: 2,
          }}
        >
          <Stack>
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '800',
                marginBottom: '9px',
              }}
            >
              {submission.kind === 'CREATE' ? 'הצעת מקום חדש' : 'הצעת משתמש'}
            </Typography>

            <Typography>{submission.original?.name || submission.proposed?.name}</Typography>
            <Typography>{submission.submissionId}</Typography>
          </Stack>

          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
        <Stack
          sx={{
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarGutter: 'stable',
            padding: '15px',
            alignContent: 'start',
            gap: '24px',
          }}
        >
          <Stack
            sx={{
              border: '1px solid var(--line)',
              borderRadius: '14px',
              padding: '16px',
              background: '#fff',
            }}
          >
            <Typography>פרטי הפונה</Typography>
            <Stack
              direction="row"
              sx={{
                gap: 10,
              }}
            >
              <Stack>
                <Typography>שם</Typography>
                <Typography>
                  {submission.reporter.firstName} {submission.reporter.lastName}
                </Typography>
              </Stack>
              <Stack>
                <Typography>טלפון</Typography>
                <Typography>{submission.reporter.phone}</Typography>
              </Stack>
              <Stack>
                <Typography>אימייל</Typography>
                <Typography>{submission.reporter.email}</Typography>
              </Stack>
              <Stack>
                <Typography>שפה</Typography>
                <Typography>{submission.languageCode}</Typography>
              </Stack>
            </Stack>
            <Button
              disabled={submission.reporterPhoneBlocked}
              onClick={() => {
                const reason = window.prompt('סיבת החסימה (הערה פנימית)') || '';
                onBlock(submission.reporter.phone, reason);
              }}
              sx={{
                color: '#b42318',
                borderColor: '#f1b9b4',
                background: '#fff5f4',
              }}
            >
              {submission.reporterPhoneBlocked ? 'הטלפון חסום' : 'חסימת הטלפון מהצעות נוספות'}
            </Button>
          </Stack>

          <Stack
            sx={{
              border: '1px solid var(--line)',
              borderRadius: '14px',
              padding: '16px',
              background: '#fff',
              gap: 5,
            }}
          >
            <Stack
              direction="row"
              sx={{
                gap: '12px',
                mb: 2,
                flexWrap: 'wrap',
              }}
            >
              {['name', 'address', 'city', 'country'].map((key) => (
                <Stack key={key}>
                  {
                    {
                      name: 'שם',
                      address: 'כתובת',
                      city: 'עיר',
                      country: 'מדינה',
                    }[key]
                  }
                  <TextField
                    value={proposed?.[key] || ''}
                    onChange={(e) => update(key, e.target.value)}
                    fullWidth
                  />
                  <Typography>קיים: {submission.original?.[key] || '-'}</Typography>
                </Stack>
              ))}
            </Stack>

            <OpeningHoursEditor days={hours} onDaysChange={setHours} />

            <Stack>
              פוליגון מוצע
              <Typography>לחיצה מוסיפה נקודה; אפשר לגרור נקודות קיימות.</Typography>
            </Stack>

            <PolygonEditor
              polygonOnly={submission.kind !== 'CREATE'}
              showHeightControl={false}
              placeForm={{
                footprintPoints: parts[0] || [],
                footprintParts: parts,
                footprintHeightLevel: 1,
                footprintMetadata: null,
                lat:
                  proposed?.location?.coordinates?.[1] ??
                  submission.original?.location?.coordinates?.[1] ??
                  parts[0]?.[0]?.[1] ??
                  29.5577,
                lng:
                  proposed?.location?.coordinates?.[0] ??
                  submission.original?.location?.coordinates?.[0] ??
                  parts[0]?.[0]?.[0] ??
                  34.9519,
                name: {
                  he: submission.original?.name || '',
                },
              }}
              setPlaceForm={(updater) => {
                const current = {
                  footprintPoints: parts[0] || [],
                  footprintParts: parts,
                  footprintHeightLevel: 1,
                  lat:
                    proposed?.location?.coordinates?.[1] ??
                    submission.original?.location?.coordinates?.[1] ??
                    parts[0]?.[0]?.[1] ??
                    29.5577,
                  lng:
                    proposed?.location?.coordinates?.[0] ??
                    submission.original?.location?.coordinates?.[0] ??
                    parts[0]?.[0]?.[0] ??
                    34.9519,
                };
                const next = typeof updater === 'function' ? updater(current) : updater;
                setParts(next.footprintParts || [next.footprintPoints || []]);
                if (
                  submission.kind === 'CREATE' &&
                  Number.isFinite(Number(next.lng)) &&
                  Number.isFinite(Number(next.lat))
                ) {
                  setProposed((current) => ({
                    ...current,
                    location: {
                      type: 'Point',
                      coordinates: [Number(next.lng), Number(next.lat)],
                    },
                  }));
                }
              }}
              originalPoints={footprintParts(submission.original?.footprint)[0] || []}
              nearbyFootprints={[]}
              nearbyFootprintsLoading={false}
              setFootprintCollisions={() => {}}
            />
            <Stack>
              הערה פנימית
              <TextField
                rows="3"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                multiline
                fullWidth
              />
            </Stack>
            {!!error && (
              <Typography
                sx={{
                  borderColor: '#fecdca',
                  background: '#fef3f2',
                  color: 'var(--danger)',
                  fontWeight: '700',
                }}
              >
                {error}
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: '16px',
            padding: '20px 24px',
            borderBottom: '0',
          }}
        >
          <Button
            onClick={onClose}
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            סגירה
          </Button>

          {submission.status === 'PENDING' && (
            <>
              <Button
                disabled={loading}
                onClick={() => run(onSave)}
                sx={{
                  background: '#edf2f7',
                  color: 'var(--text)',
                  '&:hover': {
                    background: '#ddebff',
                    color: '#1269e8',
                  },
                }}
              >
                שמירת טיוטה
              </Button>
              <Button
                disabled={loading}
                onClick={() => run(onReject)}
                sx={{
                  color: '#b42318',
                  borderColor: '#f1b9b4',
                  background: '#fff5f4',
                }}
              >
                דחיית ההצעה
              </Button>
              <Button disabled={loading} onClick={() => run(onApprove)}>
                אישור והחלה על המקום
              </Button>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
export default function PlaceEditSubmissionsPage({
  submissions,
  filters,
  setFilters,
  loadSubmissions,
  onSave,
  onApprove,
  onReject,
  onBlock,
  blockedPhones,
  onUnblock,
  loading,
}) {
  const [selected, setSelected] = useState(null);
  const [showBlocked, setShowBlocked] = useState(false);
  const columns = [
    {
      key: 'submissionId',
      label: 'מזהה',
      render: (item) => item.submissionId,
    },
    {
      key: 'place',
      label: 'מקום',
      render: (item) => item.original?.name || item.proposed?.name,
    },
    {
      key: 'reporter',
      label: 'פונה',
      render: (item) => `${item.reporter.firstName} ${item.reporter.lastName}`,
    },
    {
      key: 'phone',
      label: 'טלפון',
      render: (item) => item.reporter.phone,
    },
    {
      key: 'status',
      label: 'סטטוס',
      render: (item) => (
        <Typography
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: '26px',
            borderRadius: '999px',
            padding: '0 11px',
            fontWeight: '800',
            fontSize: '12px',
            background: '#eef2f6',
            color: '#344054',
          }}
        >
          {statusLabel[item.status]}
        </Typography>
      ),
    },
    {
      key: 'createdAt',
      label: 'נוצר',
      render: (item) => formatDate(item.createdAt),
    },
  ];
  return (
    <Stack
      sx={{
        gap: '22px',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <PageHeader title={<>הצעות עריכת מקומות</>} meta={<>{submissions.total} הצעות</>}>
        <FilterPanel>
          <TextField
            placeholder="מזהה, מקום, שם, טלפון או אימייל"
            value={filters.editSearch}
            onChange={(e) =>
              setFilters({
                ...filters,
                editSearch: e.target.value,
              })
            }
            fullWidth
          />

          <TextField
            sx={{
              minWidth: '200px',
              maxWidth: '200px',
            }}
            value={filters.editStatus}
            onChange={(e) =>
              setFilters({
                ...filters,
                editStatus: e.target.value,
              })
            }
            select
          >
            <MenuItem value="">כל הסטטוסים</MenuItem>
            <MenuItem value="PENDING">ממתין</MenuItem>
            <MenuItem value="APPROVED">אושר</MenuItem>
            <MenuItem value="REJECTED">נדחה</MenuItem>
          </TextField>

          <Button onClick={() => loadSubmissions(1)}>סינון</Button>

          <Button
            onClick={() => setShowBlocked((current) => !current)}
            sx={{
              width: 160,
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            {showBlocked ? 'הסתרת חסימות' : 'מספרים חסומים'}
          </Button>
        </FilterPanel>
      </PageHeader>
      {showBlocked && (
        <Stack
          sx={{
            border: '1px solid var(--line)',
            borderRadius: '14px',
            padding: '16px',
            background: '#fff',
          }}
        >
          <Typography>מספרי טלפון חסומים</Typography>
          {!blockedPhones.length && (
            <Typography
              sx={(theme) => ({
                color: 'var(--muted)',
                [theme.breakpoints.down('lg')]: {
                  margin: '4px 0 0',
                  fontSize: '12px',
                  display: 'none',
                },
                margin: '2px 0 0',
                fontSize: '11px',
              })}
            >
              אין מספרים חסומים.
            </Typography>
          )}
          {blockedPhones
            .filter((item) => item.isActive)
            .map((item) => (
              <Stack key={item.phoneNormalized}>
                <Typography>{item.phoneNormalized}</Typography>
                <Typography>{item.reason || 'ללא סיבה'}</Typography>
                <Typography>{item.blockedBy?.name || ''}</Typography>
                <Button
                  onClick={() => onUnblock(item.phoneNormalized)}
                  sx={{
                    background: '#edf2f7',
                    color: 'var(--text)',
                    '&:hover': {
                      background: '#ddebff',
                      color: '#1269e8',
                    },
                  }}
                >
                  ביטול חסימה
                </Button>
              </Stack>
            ))}
        </Stack>
      )}
      <DataTable
        columns={columns}
        items={submissions.items}
        rowKey={(item) => item.submissionId}
        onRowClick={setSelected}
        page={submissions.page}
        limit={submissions.limit}
        total={submissions.total}
        onPageChange={loadSubmissions}
        loading={loading}
      />
      {selected && (
        <SubmissionDialog
          submission={selected}
          onClose={() => setSelected(null)}
          onSave={onSave}
          onApprove={onApprove}
          onReject={onReject}
          onBlock={onBlock}
          loading={loading}
        />
      )}
    </Stack>
  );
}
